<template>
  <ais-instant-search
    :search-client="searchClient"
    :on-state-change="clearFilters"
    :routing="routing"
    :index-name="indexName">
    <SearchResultsPage />
  </ais-instant-search>
</template>

<script>
import Client from '@searchkit/instantsearch-client';
import Searchkit from 'searchkit';
import SearchResultsPage from './search_results_page.vue';
import { history } from 'instantsearch.js/es/lib/routers';
import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings';
import { GeospatialQueryBuilder } from './utils/geospatial_query_builder.js';

// Constants
const ELASTICSEARCH_INDEX_NAME = 'offers-' + process.env.RAILS_ENV + '-v7';
const LOCATION_STORAGE_KEY = 'supergold_selected_location';
const LOCATION_EXPIRY_HOURS = 24;
const ADDRESSES_NESTED_PATH = 'addresses';
const ADDRESSES_AGG_PATH = 'addresses.';
const AVAILABILITY_FIELD = 'addresses.type';

const ELASTICSEARCH_PUBLIC_ENDPOINT = () => {
  if (['development', 'test'].includes(process.env.RAILS_ENV)) {
    return 'http://localhost:9200';
  } else {
    return 'https://d7pi30f5y3.execute-api.ap-southeast-2.amazonaws.com/offers/' + process.env.RAILS_ENV;
  }
};


// Helper function to get location data
const getLocationData = () => {
  // First check URL parameters
  const params = new URLSearchParams(window.location.search);
  let lat = params.get('location[lat]');
  let lng = params.get('location[lng]');
  let radius = params.get('location[radius]');
  let address = params.get('location[address]');
  
  // If no URL parameters, check localStorage
  if (!lat && !lng) {
    try {
      const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (stored) {
        const locationData = JSON.parse(stored);
        
        // Check if data is less than 24 hours old
        const expiryTime = LOCATION_EXPIRY_HOURS * 60 * 60 * 1000;
        if (Date.now() - locationData.timestamp <= expiryTime) {
          lat = locationData.lat;
          lng = locationData.lng;
          radius = locationData.radius;
          address = locationData.address;
        }
      }
    } catch (error) {}
  }
  
  return {
    lat: lat ? parseFloat(lat) : null,
    lng: lng ? parseFloat(lng) : null,
    radius: radius ? parseInt(radius) : null,
    address: address
  };
};

// Helper function to check if location filtering should be applied
const shouldApplyLocationFiltering = (locationData) => {
  // Apply location filtering if we have valid location data, regardless of URL parameters
  // This ensures that localStorage location data is used for search filtering
  return locationData.lat && locationData.lng && locationData.radius;
};

// Helper function to detect if request has a meaningful text query
const hasTextQuery = (request) => {
  if (!request.body?.query?.bool) return false;
  
  return (
    // Check if must clause exists and is an array
    Array.isArray(request.body.query?.bool?.must) && 
    request.body.query.bool.must.some(clause => 
      clause.multi_match || clause.match || clause.query_string
    )
  ) || (
    // Check for query_string at root level
    request.body.query?.query_string?.query && 
    request.body.query.query_string.query.trim() !== ''
  ) || (
    // Check for simple query string
    request.body.query?.simple_query_string?.query &&
    request.body.query.simple_query_string.query.trim() !== ''
  );
};

// Helper function to extract availability filters from request
const extractAvailabilityFilters = (request) => {
  const filters = [];
  
  if (request.body?.query?.bool?.filter) {
    // Look for Searchkit's availability filter structure
    const searchkitFilter = request.body.query.bool.filter.find(f => 
      f.bool?.should?.some(shouldClause =>
        shouldClause.nested?.path === ADDRESSES_NESTED_PATH &&
        shouldClause.nested?.query?.bool?.should?.some(innerClause =>
          innerClause.term?.[AVAILABILITY_FIELD]
        )
      )
    );
    
    if (searchkitFilter) {
      // Extract the availability values from Searchkit's structure
      const availabilityValues = [];
      searchkitFilter.bool.should.forEach(shouldClause => {
        if (shouldClause.nested?.path === ADDRESSES_NESTED_PATH) {
          shouldClause.nested.query.bool.should.forEach(innerClause => {
            if (innerClause.term?.[AVAILABILITY_FIELD]) {
              availabilityValues.push(innerClause.term[AVAILABILITY_FIELD]);
            }
          });
        }
      });
      
      return availabilityValues;
    }
  }
  
  return filters;
};

// Helper function to apply geo filtering to request
const applyGeoFiltering = (request, locationData, availabilityFilters) => {
  try {
    const geoQueryBuilder = new GeospatialQueryBuilder(locationData, availabilityFilters);
    const geoFilter = geoQueryBuilder.buildGeoFilter();
    
    // Remove ALL existing availability filters when geo-filtering is applied
    // This ensures our geo-filter has complete control over availability filtering
    if (request.body.query.bool.filter) {
      const originalFilterCount = request.body.query.bool.filter.length;
      request.body.query.bool.filter = request.body.query.bool.filter.filter(filterClause => {
        // Remove nested filters that target addresses or availability
        if (filterClause.nested && filterClause.nested.path === ADDRESSES_NESTED_PATH) {
          return false;
        }
        return true;
      });
      
    }
    
    // Add geo filter to the bool query's filter array
    if (!request.body.query.bool.filter) {
      request.body.query.bool.filter = [];
    }
    request.body.query.bool.filter.push(geoFilter);
    
    
    // Add distance script field for display
    if (!request.body.script_fields) {
      request.body.script_fields = {};
    }
    request.body.script_fields.min_distance = geoQueryBuilder.buildDistanceScript();
    
    return request;
  } catch (error) {
    return request;
  }
};

// Helper function to apply smart sorting to request
const applySmartSorting = (request, hasText, shouldApplyLocation, locationData) => {
  if (!request.body?.sort) return request;
  
  try {
    const params = new URLSearchParams(window.location.search);
    const hasExplicitLocation = params.get('location[lat]') && params.get('location[lng]');
    
    if (hasText) {
      // Text search: Always prioritize relevance first
      if (shouldApplyLocation) {
        const geoQueryBuilder = new GeospatialQueryBuilder(locationData, []);
        const geoSort = geoQueryBuilder.buildGeoSort();
        request.body.sort = [
          { _score: { order: 'desc' } },
          geoSort,
          { start_date: { order: 'desc' } }
        ];
      } else {
        // No location: pure relevance
        request.body.sort = [
          { _score: { order: 'desc' } },
          { start_date: { order: 'desc' } }
        ];
      }
    } else if (shouldApplyLocation) {
      // Location-based browsing: Only sort by distance first if location was explicitly set
      const geoQueryBuilder = new GeospatialQueryBuilder(locationData, []);
      const geoSort = geoQueryBuilder.buildGeoSort();
      
      if (hasExplicitLocation) {
        // Explicit location search: Distance first
        request.body.sort = [geoSort, ...request.body.sort];
      } else {
        // localStorage location: Add distance as secondary sort
        request.body.sort = [
          { _score: { order: 'desc' } },
          geoSort,
          { start_date: { order: 'desc' } }
        ];
      }
    }
    // No text query and no location: keep default sorting
    
    return request;
  } catch (error) {
    return request;
  }
};

// Helper function to apply availability aggregation fixes
const applyAggregationFixes = (request, locationData) => {
  if (!request.body?.aggs?.[ADDRESSES_AGG_PATH]?.aggs?.availability) return request;
  
  try {
    const aggGeoQueryBuilder = new GeospatialQueryBuilder(locationData, []);
    request.body.aggs[ADDRESSES_AGG_PATH].aggs.availability = aggGeoQueryBuilder.buildAvailabilityAggregation();
    return request;
  } catch (error) {
    return request;
  }
};

// Helper function to process aggregation response
const processAggregationResponse = (result) => {
  if (!result?.aggregations?.[ADDRESSES_AGG_PATH]?.availability) return result;
  
  try {
    const availabilityAgg = result.aggregations[ADDRESSES_AGG_PATH].availability;
    
    
    if (availabilityAgg.availability?.buckets) {
      // Move the nested availability buckets to the expected location
      result.aggregations[ADDRESSES_AGG_PATH].availability = availabilityAgg.availability;
      // Replace nested address counts with reverse_nested offer counts
      result.aggregations[ADDRESSES_AGG_PATH].availability.buckets = availabilityAgg.availability.buckets.map(bucket => ({
        ...bucket,
        doc_count: bucket.offer_count ? bucket.offer_count.doc_count : bucket.doc_count
      }));
    } else if (availabilityAgg.buckets) {
      // Fallback for old structure
      availabilityAgg.buckets = availabilityAgg.buckets.map(bucket => ({
        ...bucket,
        doc_count: bucket.offer_count ? bucket.offer_count.doc_count : bucket.doc_count
      }));
    }
    
    
    return result;
  } catch (error) {
    return result;
  }
};

// Main search request modification function
const applySmartSearchLogic = (request) => {
  if (typeof request === 'string') return request; // Skip index lines
  if (!request.body?.query?.bool) return request; // Skip non-search requests
  
  const locationData = getLocationData();
  const shouldApplyLocation = shouldApplyLocationFiltering(locationData);
  const hasText = hasTextQuery(request);
  
  
  // Apply location-based filtering if needed
  if (shouldApplyLocation) {
    const availabilityFilters = extractAvailabilityFilters(request);
    request = applyGeoFiltering(request, locationData, availabilityFilters);
    request = applyAggregationFixes(request, locationData);
  }
  
  // Apply smart sorting for all searches
  request = applySmartSorting(request, hasText, shouldApplyLocation, locationData);
  
  
  return request;
};

const config = {
  connection: {
    host: ELASTICSEARCH_PUBLIC_ENDPOINT(),
  },
  search_settings: {
    fuzziness: "AUTO:6,9",  // Only fuzzy match words 6+ characters (prevents "cafe" matching "care")
    search_attributes: [
      { field: 'provider', weight: 3 },
      { field: 'subcategories', weight: 3 },
      { field: 'headline', weight: 2 },
      { field: 'keywords', weight: 2 },
      { field: 'category', weight: 2 },
      { field: 'detail', weight: 1 },
    ],
    result_attributes: ['headline', 'amount', 'provider', 'provider_image', 'provider_image_url', 'provider_image_description',  'id', 'category_id', 'start_date', 'expiring_offer', 'addresses'],
    sorting: {
      default: [
        {
          field: '_score',
          order: 'desc',
        },
        {
          field: 'start_date',
          order: 'desc',
        },
      ],
    },
    facet_attributes: [
      {
        attribute: 'super_category',
        field: 'super_category.keyword',
        type: 'string',
      },
      { attribute: 'category',
        field: 'category.keyword',
        type: 'string',
      },
      { attribute: 'subcategories',
        field: 'subcategories.keyword',
        type: 'string',
      },
      { attribute: 'availability',
        field: 'type',
        type: 'string',
        nestedPath: 'addresses'
      },
      {
        attribute: 'tags',
        field: 'tags.keyword',
        type: 'string',
      },
    ],
    filters: [],
    query_rules: [],
  },
};

// Create custom SearchKit class using the clean helper functions
class SmartSearchkit extends Searchkit {
  constructor(config) {
    super(config);
  }
  
  async performSearch(requestBody, requestOptions) {
    // Apply smart search logic to each request
    const modifiedBody = requestBody.map(request => {
      return applySmartSearchLogic(request);
    });
    
    // Execute the search
    const response = await super.performSearch(modifiedBody, requestOptions);
    
    // Process response for location-based results
    const locationData = getLocationData();
    const shouldApplyLocation = shouldApplyLocationFiltering(locationData);
    
    if (shouldApplyLocation && Array.isArray(response)) {
      const modifiedResponse = response.map(result => processAggregationResponse(result));
      
      
      return modifiedResponse;
    }
    
    return response;
  }
}

// Create Searchkit client with custom class
const searchkitClient = new SmartSearchkit(config);

const searchClient = Client(searchkitClient);

export default {
  name: 'SearchResultsApp',
  components: {
    SearchResultsPage,
  },
  mounted() {
    // Make this component available globally for location search integration
    window.searchApp = this;
  },
  data() {
    return {
      routing: {
        router: history(),
        stateMapping: singleIndexMapping(ELASTICSEARCH_INDEX_NAME),
      },
      indexName: ELASTICSEARCH_INDEX_NAME,
      searchClient,
    };
  },
  methods: {
    clearFilters({ uiState, setUiState}) {
      if (uiState[ELASTICSEARCH_INDEX_NAME]?.menu?.category === undefined) {
        delete uiState[ELASTICSEARCH_INDEX_NAME]?.menu?.subcategories;
      }

      // Remove old region/district logic since we're now using location-based search
      // Location-based filters will be handled by the backend search integration

      setUiState(uiState);
    },
  },
};
</script>
